import React, { useRef, useState, ClipboardEvent, KeyboardEvent } from 'react';
import { FormGroup } from '@patternfly/react-core';

interface OtpInputProps {
	onChange: (otp: string) => void;
	userTokenExpiry?: number;
}

export const OtpInput: React.FC<OtpInputProps> = ({ onChange, userTokenExpiry }) => {
	const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	const updateDigits = (newDigits: string[]) => {
		setDigits(newDigits);
		onChange(newDigits.join(''));
	};

	const handleChange = (index: number, value: string) => {
		const digit = value.replace(/\D/g, '').slice(-1);
		const newDigits = [...digits];
		newDigits[index] = digit;
		updateDigits(newDigits);
		if (digit && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Backspace') {
			e.preventDefault();
			if (digits[index]) {
				const newDigits = [...digits];
				newDigits[index] = '';
				updateDigits(newDigits);
			} else if (index > 0) {
				const newDigits = [...digits];
				newDigits[index - 1] = '';
				updateDigits(newDigits);
				inputRefs.current[index - 1]?.focus();
			}
		} else if (e.key === 'ArrowLeft' && index > 0) {
			inputRefs.current[index - 1]?.focus();
		} else if (e.key === 'ArrowRight' && index < 5) {
			inputRefs.current[index + 1]?.focus();
		} else if (/^[0-9]$/.test(e.key)) {
			e.preventDefault();
			const newDigits = [...digits];
			newDigits[index] = e.key;
			updateDigits(newDigits);
			if (index < 5) {
				inputRefs.current[index + 1]?.focus();
			}
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
		if (!pasted) return;
		const newDigits = [...digits];
		pasted.split('').forEach((char, i) => {
			if (i < 6) newDigits[i] = char;
		});
		updateDigits(newDigits);
		const nextEmpty = newDigits.findIndex((d) => !d);
		const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
		inputRefs.current[focusIndex]?.focus();
	};

	return (
		<FormGroup
			label="Verification Code"
			isRequired
			helperText={`The code expires in ${userTokenExpiry ?? 30} minutes.`}
		>
			<div
				style={{
					display: 'flex',
					gap: '10px',
					marginTop: '8px',
				}}
			>
				{digits.map((digit, index) => (
					<input
						key={index}
						ref={(el) => (inputRefs.current[index] = el)}
						type="text"
						inputMode="numeric"
						pattern="[0-9]"
						maxLength={1}
						value={digit}
						data-testid={`mfa-otp-${index}`}
						aria-label={`Digit ${index + 1} of 6`}
						onKeyDown={(e) => handleKeyDown(index, e)}
						onPaste={handlePaste}
						style={{
							width: '44px',
							height: '52px',
							textAlign: 'center',
							fontSize: '22px',
							fontWeight: 500,
							border: digit
								? '2px solid var(--pf-global--primary-color--100, #06c)'
								: '1.5px solid #ccc',
							borderRadius: '8px',
							outline: 'none',

							transition: 'border-color 0.15s',
							background: 'var(--pf-global--BackgroundColor--100, #fff)',
							color: 'var(--pf-global--Color--100, #151515)',
						}}
					/>
				))}
			</div>
		</FormGroup>
	);
};
